import React, { useEffect, useMemo, useState } from "react";
import { Dimensions, StyleSheet, Pressable, View } from "react-native";

import {
  DATE_FORMAT,
  READABLE_DATE_FORMAT,
  TABLET_PC,
} from "libs/utils/constants";
import { isSameDay } from "libs/utils/dateUtils";
import moment from "moment";
import FlatList from "./flatlist";
import Text from "./text";
import translate from "libs/locale";
import variables from "src/common/assets/variables";
import { TimeSlot } from "../types/response/master";

const { width } = Dimensions.get("window");
const ITEM_WIDTH = TABLET_PC ? 85 : width * 0.14;
const ITEM_HEIGHT = 70;
const ITEM_OFFSET = ITEM_WIDTH + 18;

interface Props {
  selectedDate: string;
  maxDateLength: number;
  maxDate: number;
  timeSlots: TimeSlot[];
  serverTime: moment.Moment;
  setSelectedDate: (date: moment.Moment) => void;
}

interface DateArray {
  date: moment.Moment;
  enabled: boolean;
}

function isToday(date: moment.Moment, serverTime: moment.Moment): boolean {
  return (
    date?.isSame(serverTime, "year") &&
    date?.isSame(serverTime, "month") &&
    date?.isSame(serverTime, "day")
  );
}

function generateHorizontalCalendarDates(
  serverTime: moment.Moment,
  days: number,
  maxDate: number,
  timeSlots: TimeSlot[]
): Array<DateArray> {
  let today = moment(serverTime);
  let result = [];
  const lastAvailableTime = timeSlots[timeSlots.length - 2];
  const lastTimeUnix = moment(lastAvailableTime?.name, "HH:mm A");
  // adding a 1 hour threshold
  const isPastToday = moment(serverTime).add(1, "hours").isAfter(lastTimeUnix);
  if (isPastToday) {
    today = today.add(1, "days");
  }
  for (let i = 0; i < days; i++) {
    result[i] = {
      date: today.clone().add(i, "days"),
      enabled: i < maxDate,
    };
  }

  return result;
}

const calculateScrolledMonth = (dates, scrollPosition) => {
  return moment(dates[0].date)
    .add(scrollPosition / (ITEM_WIDTH - 8), "days")
    .format("MMMM");
};

export default function HorizontalCalendar({
  selectedDate,
  maxDateLength,
  serverTime,
  maxDate,
  timeSlots,
  setSelectedDate,
}: Props) {
  const dates: Array<DateArray> = useMemo(() => {
    return generateHorizontalCalendarDates(
      serverTime,
      maxDateLength,
      maxDate,
      timeSlots
    );
  }, []);

  const [currentMonth, setCurrentMonth] = useState<string>(
    calculateScrolledMonth(dates, 0)
  );

  const onDatePress = (date: moment.Moment) => {
    setSelectedDate(date);
  };

  const getCurrentMonth = (scrollPosition) => {
    setCurrentMonth(calculateScrolledMonth(dates, scrollPosition));
  };

  const onScrolled = (e) => {
    getCurrentMonth(e.nativeEvent.contentOffset.x);
  };

  const renderItem = ({ item, index }: { item: DateArray; index: number }) => {
    const dayNumber = item.date.format("DD");
    const dayString = item.date.format("ddd");
    const isActive = selectedDate
      ? selectedDate === moment(item.date).format(DATE_FORMAT)
      : index === 0;
    return (
      <Pressable
        onPress={() => onDatePress(item.date)}
        style={[styles.item, isActive && styles.selectedDay]}
        disabled={!item.enabled}
      >
        <Text
          style={[
            styles.dayStyle,
            isActive && styles.activeText,
            !item.enabled && styles.disabledText,
          ]}
        >
          {isToday(item.date, serverTime) ? translate("today") : dayString}
        </Text>
        <Text
          style={[
            styles.dayNumber,
            styles.dateOutput,
            isActive && styles.activeText,
            !item.enabled && styles.disabledText,
          ]}
        >
          {dayNumber}
        </Text>
      </Pressable>
    );
  };

  return (
    <View style={styles.root}>
      <Text style={styles.month}>{currentMonth}</Text>
      <FlatList
        data={dates}
        renderItem={renderItem}
        // onScroll={onScrolled}
        onScrollEndDrag={onScrolled}
        keyExtractor={(item) => item.date.format(DATE_FORMAT)}
        horizontal={true}
        contentContainerStyle={styles.contentContainerStyle}
        showsHorizontalScrollIndicator={false}
        initialScrollIndex={0}
        getItemLayout={(_, index) => ({
          length: ITEM_WIDTH,
          offset: ITEM_OFFSET * index,
          index,
        })}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    backgroundColor: "#fff",
  },
  contentContainerStyle: {
    padding: 8,
  },
  dateOutput: {
    color: variables.$textDark,
    fontSize: 18,
  },
  dayStyle: {
    color: variables.$textDark,
    fontSize: 12,
  },
  activeText: {
    color: variables.$brandColor,
    fontFamily: "MontserratBold",
  },
  item: {
    width: ITEM_WIDTH,
    height: ITEM_HEIGHT,
    justifyContent: "center",
    alignItems: "center",
  },
  disabledText: {
    color: variables.$disabledText,
  },
  dayNumber: {
    marginTop: 4,
  },
  selectedDay: {
    borderRadius: 15,
    backgroundColor: variables.$highlight,
  },
  month: {
    color: variables.$textDark,
    marginLeft: 8,
    fontFamily: "MontserratBold",
    marginTop: 8,
  },
});
